import {
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  collection,
  onSnapshot,
  query,
  where,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import { audioApi, pdfApi, taskApi, videoApi, wordApi } from 'api';
import injectEntitiesIntoAnotherEntitiesByKey from 'shared/helpers/injectEntitiesIntoAnotherEntitiesByKey';
import { HW, WorkType } from 'shared/types';
import { SummarizeTask } from 'shared/types/task';

import { imgApi } from './img-api';
import { getStudentView } from '../Common/Database';
import { firestore } from '../Common/Firebase';
import { PersonOption } from '../shared/types/works';

const colName = 'Works';
const colRef = collection(firestore, colName);
const getDocRef = (HWId: HW['id']) => doc(firestore, colName, HWId);

async function addHW(HW: HW) {
  const newId = uuidv4();
  const newHW = { ...HW, id: newId }; // add or overwrite HW ID

  const docRef = getDocRef(newId);

  await setDoc(docRef, newHW);

  return newHW;
}

async function updateHW(HW: HW) {
  const docRef = getDocRef(HW.id);

  await updateDoc(docRef, { ...HW });
}

async function removeHW(id: HW['id']) {
  const tasks: SummarizeTask[] = await taskApi.getICLTasks(id);
  await taskApi.removeICLTasks(id, tasks);

  tasks.forEach(async task => {
    task.audioUrl && (await audioApi.removeAudio(task.audioUrl, id, task.id));
    task.videoUrl && (await videoApi.removeVideo(task.videoUrl, id, task.id));

    task.pdfUrl && (await pdfApi.removePdf(task.pdfUrl, id, task.id));
    task.wordUrl && (await wordApi.removeWord(task.wordUrl, id, task.id));
    task.studentWordUrl && (await wordApi.removeWord(task.studentWordUrl, id, task.id, true));
    task.studentPdfUrl && (await pdfApi.removePdf(task.studentPdfUrl, id, task.id, true));

    task.taskPhotos &&
      task.taskPhotos.forEach(
        async photo => await imgApi.removeImg(photo, id, task.id, task.taskPhotos as string[], 'taskPhotos'),
      );
    task.answersPhotos &&
      task.answersPhotos.forEach(
        async photo => await imgApi.removeImg(photo, id, task.id, task.answersPhotos as string[], 'answersPhotos'),
      );
  });

  const docRef = getDocRef(id);
  await deleteDoc(docRef);
}

interface SubscribeToAdminHWsParams {
  setNewHWs: (HWs: HW[]) => void;
  studentFilter?: PersonOption | null;
  teacherFilter?: PersonOption | null;
  subjectFilter?: string | null;
  classFilter?: string | null;
}

function subscribeToAdminHWs({
  setNewHWs,
  classFilter,
  subjectFilter,
  teacherFilter,
  studentFilter,
}: SubscribeToAdminHWsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.hw));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (teacherFilter) {
    queryRef = query(queryRef, where('teacherEmail', '==', teacherFilter.email));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  return onSnapshot(queryRef, snapshot => {
    const HWs: HW[] = [];
    snapshot.forEach(doc => HWs.push(doc.data() as HW));
    setNewHWs(HWs);
  });
}

function subscribeToAdminConsiderationHWs({
  setNewHWs,
  classFilter,
  subjectFilter,
}: Omit<SubscribeToAdminHWsParams, 'studentFilter' | 'teacherFilter'>) {
  let queryRef = query(colRef, where('type', '==', WorkType.hw), where('isConsideration', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const HWs: HW[] = [];
    snapshot.forEach(doc => HWs.push(doc.data() as HW));
    setNewHWs(HWs);
  });
}

interface SubscribeToTeacherHWsParams {
  setNewHWs: (HWs: HW[] | ((Ws: HW[]) => HW[])) => void;
  teacherEmail: string;
  isActive?: boolean;
  subjectFilter?: string | null;
  classFilter?: string | null;
  studentFilter?: PersonOption | null;
  myICLs?: boolean;
  studentsWorks?: boolean;
}

function subscribeToTeacherHWs({
  setNewHWs,
  teacherEmail,
  isActive = true,
  classFilter,
  subjectFilter,
  studentFilter,
  myICLs,
  studentsWorks,
}: SubscribeToTeacherHWsParams) {
  let queryRef = query(
    colRef,
    where('type', '==', WorkType.hw),
    where('teacherEmail', '==', teacherEmail),
    where('isActive', '==', isActive),
  );

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  if (studentFilter) {
    queryRef = query(queryRef, where('studentEmail', '==', studentFilter.email));
  }

  return onSnapshot(queryRef, snapshot => {
    let HWs: HW[] = [];
    snapshot.forEach(doc => HWs.push(doc.data() as HW));

    if (studentsWorks) {
      HWs = HWs.filter(HW => HW.studentEmail);
    }

    if (myICLs) {
      HWs = HWs.filter(HW => HW.teacherEmail === teacherEmail && !HW.studentEmail);
    }

    injectEntitiesIntoAnotherEntitiesByKey<HW, PersonOption>(HWs, 'studentEmail', 'student', uniqueKeys =>
      Promise.all(uniqueKeys.map(getStudentView)),
    ).then(setNewHWs);

    setNewHWs(HWs);
  });
}

interface SubscribeToTemplateHWsParams {
  setNewHWs: (HWs: HW[]) => void;
  subjectFilter?: string | null;
  classFilter?: string | null;
}

function subscribeToTemplateHWs({ setNewHWs, classFilter, subjectFilter }: SubscribeToTemplateHWsParams) {
  let queryRef = query(colRef, where('type', '==', WorkType.hw), where('isTemplate', '==', true));

  if (classFilter) {
    queryRef = query(queryRef, where('class', '==', classFilter));
  }

  if (subjectFilter) {
    queryRef = query(queryRef, where('subject.id', '==', subjectFilter));
  }

  return onSnapshot(queryRef, snapshot => {
    const HWs: HW[] = [];
    snapshot.forEach(doc => HWs.push(doc.data() as HW));
    setNewHWs(HWs);
  });
}

interface SubscribeToStudentHWsParams {
  setNewHWs: (HWs: HW[]) => void;
  studentEmail: string;
}

function subscribeToStudentHWs({ setNewHWs, studentEmail }: SubscribeToStudentHWsParams) {
  const queryRef = query(
    colRef,
    where('type', '==', WorkType.hw),
    where('studentEmail', '==', studentEmail),
    where('isActive', '==', true),
  );

  return onSnapshot(queryRef, snapshot => {
    const HWs: HW[] = [];
    snapshot.forEach(doc => HWs.push(doc.data() as HW));
    setNewHWs(HWs);
  });
}

interface SubscribeToHWByIdParams {
  HWId: HW['id'];
  setNewHW: (HW: HW) => void;
}

function subscribeToHW({ HWId, setNewHW }: SubscribeToHWByIdParams) {
  const docRef = getDocRef(HWId);

  return onSnapshot(docRef, snapshot => {
    setNewHW(snapshot.data() as HW);
  });
}

async function getHW(HWId: HW['id']) {
  const docRef = getDocRef(HWId);
  const docSnap = await getDoc(docRef);
  const HW = docSnap.data() as HW;

  return HW;
}

async function copyHWToTeacher(HW: HW, teacherEmail: HW['teacherEmail']) {
  const prevHWId = HW.id;
  const newHW: HW = {
    ...HW,
    parentId: prevHWId,
    teacherEmail,
    createdAt: Date.now(),
    isTemplate: false,
    isActive: true,
    checked: false,
    completed: false,
  };

  const newHWId = await addHW(newHW);
  await taskApi.copyICLTasks(prevHWId, newHWId.id);
}

async function copyHWToAdmin(HW: HW) {
  const prevHWId = HW.id;
  const newHW: HW = {
    ...HW,
    parentId: prevHWId,
    isTemplate: true,
    isActive: true,
    createdAt: Date.now(),
    teacherEmail: '',
    studentEmail: '',
    checked: false,
    completed: false,
  };

  const newHWId = await addHW(newHW);
  await taskApi.copyICLTasks(prevHWId, newHWId.id);
}

async function sendHWToStudents(HW: HW, studentsEmails: string[]) {
  const prevHWId = HW.id;
  const batch = writeBatch(firestore);
  const newHWIds: string[] = [];

  studentsEmails.forEach(email => {
    const newId = uuidv4();
    const newHW: HW = { ...HW, parentId: prevHWId, createdAt: Date.now(), id: newId, studentEmail: email }; // overwrite HW ID
    const docRef = getDocRef(newId);
    batch.set(docRef, newHW);
    newHWIds.push(newId);
  });

  await taskApi.prepareTasksForCopying(prevHWId, batch, newHWIds);
  await batch.commit();
}

export const hwApi = {
  getHW,
  addHW,
  removeHW,
  updateHW,
  subscribeToAdminHWs,
  subscribeToTeacherHWs,
  subscribeToStudentHWs,
  subscribeToTemplateHWs,
  subscribeToAdminConsiderationHWs,
  subscribeToHW,
  copyHWToTeacher,
  copyHWToAdmin,
  sendHWToStudents,
};
